import { CurrencyPipe } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit, PLATFORM_ID } from '@angular/core';
import { DomSanitizer, SafeResourceUrl, Title } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { LocalstorageService } from 'src/app/core/services/localstorage.service';
import { ProductsService } from 'src/app/core/services/products/products.service';
import { SeoService } from 'src/app/core/services/seo.service';
import { ShoppingCartService } from 'src/app/core/services/shopping-cart/shopping-cart.service';

import { getPricePerPound } from 'src/app/global/utils';
import { Product } from 'src/app/interfaces/products.interface';
import { ShippingMethodType } from 'src/app/interfaces/salePoints.interface';
import { User } from 'src/app/interfaces/user.interface';

import $ from 'jquery';
declare var $: $;

@Component({
  selector: 'app-product-detail',
  templateUrl: './product-detail.component.html',
  styleUrls: ['./product-detail.component.scss'],
})
export class ProductDetailComponent implements OnInit, OnDestroy {
  /** Category param */
  category: string;

  /** Saved cost by gram */
  costForGram: number;

  /** Nutri info exists */
  nutriInfo = false;

  /** Product */
  product: Product;

  /** Logged user */
  user: User;

  /** Quantity of product (B2B clients) */
  public quantityProduct: number;

  public checkProductIcon = false;

  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly activatedRoute: ActivatedRoute,
    private readonly productService: ProductsService,
    private readonly router: Router,
    private readonly shoppingCartService: ShoppingCartService,
    private readonly title: Title,
    private readonly seo: SeoService,
    private readonly currencyPipe: CurrencyPipe,
    private readonly localStorage: LocalstorageService,
    private readonly sanitizer: DomSanitizer,
    @Inject(PLATFORM_ID) private readonly platformId: object
  ) { }

  ngOnInit(): void {
    this.title.setTitle('Productos | Calypso del Caribe');

    // 1) Toma el producto del resolver (si existe)
    this.product = this.activatedRoute.snapshot.data.productdata as Product;

    // 2) Suscríbete a params (evita fugas)
    this.activatedRoute.paramMap
      .pipe(takeUntil(this.destroy$))
      .subscribe((params) => {
        this.category = params.get('category') ?? '';

        // Si el producto depende de la categoría/slug, aquí sería el lugar ideal
        // para recargarlo o validar. En tu caso ya viene del resolver.
        this.applyPageState();
        this.applySeo();
      });

    // 3) User + quantity initial
    this.user = JSON.parse(this.localStorage.getItem('user')) as User;
    this.quantityProduct = this.product?.quantity > 0 ? this.product.quantity : null;
  }

  ngOnDestroy(): void {
    this.seo.clearProductInfo();
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Aplica transformaciones/estado al producto (precio, cobertura, carrito, etc.)
   * Esto estaba en getProduct(), pero realmente no “getea” nada.
   */
  private applyPageState(): void {
    if (!this.product) return;

    const cobertura = this.localStorage.getItem('cobertura');
    const user = JSON.parse(this.localStorage.getItem('user')) as User;

    if (cobertura === 'SI' || cobertura === 'SIN-SESION') {
      this.product.cobertura = true;
    }

    this.product = this.productService.aplicaCambioPrecio(this.product);

    if (user?.idListaPrecios && this.product.listaPrecios) {
      if (user.idListaPrecios in this.product.listaPrecios) {
        this.product.precio = this.product.listaPrecios[user.idListaPrecios];
      }
    }

    this.product.add = false;
    this.product.quantity = 0;

    this.checkCart();
    this.getCostForGram(this.product);

    this.productService.onSendCategoryParam(this.product.categoria);

    this.nutriInfo = Array.isArray(this.product.infoNutricional) && this.product.infoNutricional.length >= 1;
  }

  /**
   * SEO: 1 solo lugar, 1 sola llamada (evita duplicación).
   * Nota: setProductInfo en tu service debe construir URL correcta
   * (o tú se la pasas con setSEO). Aquí lo hago explícito.
   */
  private applySeo(): void {
    if (!this.product) return;

    const productPath = this.buildProductPath(this.category, this.product.slug);

    // Llama SOLO UNA:
    // Opción A: si tu setProductInfo ya arma url internamente (recomendado)
    // this.seo.setProductInfo(this.product);

    // Opción B: setSEO con url correcta + tags producto
    this.seo.setSEO({
      title: this.product.nombre,
      description: this.product.descripcion || this.buildFallbackDescription(this.product),
      image: this.product.imagen,
      url: productPath,
      main: false,
    });
    this.seo.setProductInfo(this.product, productPath);
  }

  private buildProductPath(category: string, slug: string): string {
    const safeCategory = (category ?? '').trim();
    return safeCategory
      ? `/tienda/${encodeURIComponent(safeCategory)}/${encodeURIComponent(slug)}`
      : `/tienda/${encodeURIComponent(slug)}`;
  }

  private buildFallbackDescription(product: Product): string {
    const price = this.currencyPipe.transform(product.precio, 'USD', 'symbol', '1.0-0');
    return `Descripción: ${product.nombre}. Peso: ${product.pesoGramos}g. Precio: ${price ?? ''}`;
  }

  getCostForGram(product: Product): void {
    this.costForGram = parseFloat((product.precio / product.pesoGramos).toFixed(2));
  }

  backCategory(categoryParam: string): void {
    this.router.navigate(['/categories', categoryParam]);
  }

  public checkCart(): void {
    const cartLocalStorage = JSON.parse(this.localStorage.getItem('cart'));
    if (cartLocalStorage && cartLocalStorage.length > 0) {
      for (const productCart of cartLocalStorage) {
        if (productCart.id === this.product.id) {
          this.product.add = true;
          this.product.quantity = productCart.quantity;
          this.shoppingCartService.addCartAgain(this.product);
        }
      }
    }
  }

  public addProduct(): void {
    let shippingMethod = null;
    let cobertura = '';
    shippingMethod = JSON.parse(this.localStorage.getItem('shippingMethod'));
    cobertura = this.localStorage.getItem('cobertura');

    if (cobertura === 'SIN-SESION') {
      this.openAddressModal();
      return;
    }

    if (cobertura === 'SI') {
      let bodega;
      switch (shippingMethod.type) {
        case ShippingMethodType.IN_STORE:
          bodega = shippingMethod.salePoint.bodega;
          break;
        case ShippingMethodType.SHIPPING:
          bodega = shippingMethod.address.inventario;
          break;
        default:
          return;
      }

      if (this.product.inventarioBodega[bodega] >= this.product.quantity + 1) {
        this.product.quantity++;
        this.product.add = true;
        this.shoppingCartService.addProductsCart(this.product);
      } else {
        alert('No tenemos disponible este producto');
      }
    }
  }

  public async moreOneProduct(): Promise<void> {
    let shippingMethod = null;
    let cobertura = '';
    shippingMethod = JSON.parse(this.localStorage.getItem('shippingMethod'));
    cobertura = this.localStorage.getItem('cobertura');

    if (cobertura === 'SIN-SESION') {
      this.openAddressModal();
      return;
    }

    if (cobertura === 'SI') {
      let bodega;
      switch (shippingMethod.type) {
        case ShippingMethodType.IN_STORE:
          bodega = shippingMethod.salePoint.bodega;
          break;
        case ShippingMethodType.SHIPPING:
          bodega = shippingMethod.address.inventario;
          break;
        default:
          return;
      }

      if (this.product.inventarioBodega[bodega] >= this.product.quantity + 1) {
        this.product.quantity++;
        this.shoppingCartService.addProductsCart(this.product);
      } else {
        alert('Inventario no disponible');
      }
    }
  }

  public removeProduct(): void {
    this.product.quantity--;
    if (this.product.quantity === 0) this.product.add = false;
    this.shoppingCartService.addProductsCart(this.product);
  }

  addQuantityProduct(): void {
    if (this.quantityProduct > 0) {
      this.product.quantity = this.quantityProduct;
      this.product.add = true;
      this.shoppingCartService.addProductsCart(this.product);
      this.checkProductIcon = true;
    }
  }

  removeQuantityProduct(): void {
    this.product.quantity = 0;
    this.product.add = false;
    this.shoppingCartService.addProductsCart(this.product);
  }

  openAddressModal(): void {
    $('#openDeliverySelector')?.click();
  }

  pricePerPound(price: number, weightGramsVariable: number, weightGrams: number): number {
    return getPricePerPound(price, weightGramsVariable, weightGrams);
  }

  changeQuantityProduct(): void {
    this.checkProductIcon = false;
  }

  validateInput(event: any): void {
    const input = event.target.value;
    if (input.length > 3) event.target.value = input.slice(0, 3);
    this.quantityProduct = Number(event.target.value);
    this.changeQuantityProduct();
  }

  get formattedPesoGramos(): string {
    this.costForGram = parseFloat((this.product.precio / this.product.pesoGramos).toFixed(2));
    return this.costForGram.toString().replace('.', ',') ?? '0';
  }

  goToSlide(index: number): void {
    $('#carrouselImages').carousel(index);
  }

  isVideo(url: string): boolean {
    if (!url) return false;
    return url.endsWith('.mp4') || this.isYouTube(url) || url.includes('vimeo');
  }

  isYouTube(url: string): boolean {
    return url.includes('youtube.com') || url.includes('youtu.be');
  }

  getYouTubeEmbedUrl(url: string): SafeResourceUrl {
    if (!url) return '';
    let videoId = '';
    if (url.includes('youtube.com')) videoId = url.split('v=')[1]?.split('&')[0];
    else if (url.includes('youtu.be')) videoId = url.split('/').pop();

    const embedUrl = videoId ? `https://www.youtube.com/embed/${videoId}` : url;
    return this.sanitizer.bypassSecurityTrustResourceUrl(embedUrl);
  }

  getYouTubeThumbnail(url: string): string {
    let videoId = '';
    if (url.includes('youtube.com')) videoId = url.split('v=')[1]?.split('&')[0];
    else if (url.includes('youtu.be')) videoId = url.split('/').pop();

    return videoId
      ? `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`
      : '../../../../assets/imgs/Img-our-products/generic-img.svg';
  }
}
